// **React Imports
import {
  Fragment,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

//**Component Imports
import OverlayLoading from './components/overlay-loading'
import TenantDetailsModal from './components/tenant-details-modal'

// **Third party Imports
import 'driver.js/dist/driver.css'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './axios/global'

// **Utils Imports
import { DOMAIN, ROLES, CANDIDATE_APP_MODE_COOKIE_NAME } from './utils/constant'
import { CHECK_CONTEST_URI_REGEX } from './utils/regex'
import {
  checkCandidateAccess,
  checkDeveloperAccess,
  checkPublicRoute,
  clearLocalAndSessionStorage,
  deleteCookie,
  getCookie,
  getJWTToken,
  getUserDetails,
  setRememberMeFlag,
  showErrorToast
} from './utils/utils'

// ** Error Boundary Component
import ErrorBoundary from './pages/error'

// **Redux Imports
import { useDispatch } from 'react-redux'
import {
  setAppMode,
  setCurrentlyActivePlan,
  setSubscriptions,
  setUserInformation
} from './store/slice/auth'
import { setSelectedWorkspace } from './store/slice/workspace'

// **API Imports
import { getConfig } from './axios/api/configurations'
import { getDemoUserToken } from './axios/api/role'
import { getAllUserSubscriptions } from './axios/api/subscription'

// **Context Imports
import { SettingsContext } from './context/setting'

// **SCSS Imports
import 'swiper/css'
import './App.scss'

// **Favicon Imports
import DefaultFavicon from './assets/images/favicon.svg'

// Hook Imports
import useDemoUser from './hooks/useDemoUser'
import useMonacoErrorHandler from './hooks/useMonacoErrorHandler'
import { setCandidateAppMode } from './store/slice/candidate'

function App() {
  // custom hooks
  useMonacoErrorHandler()

  // hooks
  const dispatch = useDispatch()
  const { saveSettings } = useContext(SettingsContext)
  const location = useLocation()
  const navigate = useNavigate()

  // Variables
  const match = useMemo(() => {
    const path = ['/c/profile', '/c/subscription']

    if (path.includes(location.pathname?.toLowerCase())) {
      return null
    }
    return location.pathname.match(CHECK_CONTEST_URI_REGEX)
  }, [location.pathname])

  const contestUri = useMemo(() => {
    return match ? match[1] : null
  }, [match])

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  // state
  const [loading, setLoading] = useState({
    subscription: true,
    config: true,
    demoUserToken: true
  })
  const { settings } = useContext(SettingsContext)

  const navigateToHome = useCallback(
    (path = null) => {
      if (path) {
        navigate(path)
        return
      }
      navigate('/')
    },
    [navigate]
  )

  const { isDemoTenant } = useDemoUser()

  // API to get subscription
  const fetchUserSubscription = useCallback(async () => {
    // accessing token
    const accessToken = getJWTToken()

    if (!accessToken) {
      setLoading((prev) => ({ ...prev, subscription: false }))
      return
    }

    const response = await getAllUserSubscriptions()

    if (response?.isError) {
      dispatch(setCurrentlyActivePlan(null))
      dispatch(setSubscriptions(null))
    } else {
      const activePlan = response?.filter(
        (plan) => plan?.status?.toLowerCase() === 'active'
      )
      dispatch(setSubscriptions(response))
      dispatch(setCurrentlyActivePlan(activePlan?.[0] || null))
    }

    setLoading((prev) => ({ ...prev, subscription: false }))
  }, [dispatch])

  // API call to get config
  const fetchTenantConfig = useCallback(
    async (contestUri = null) => {
      const response = await getConfig(contestUri)

      if (response?.isError) {
        saveSettings({ tenantConfig: null })
      } else {
        saveSettings({ tenantConfig: response })
        if (response?.primaryColor) {
          sessionStorage.setItem('themeColor', response.primaryColor)
        }
      }
      setLoading((prev) => ({ ...prev, config: false }))
    },
    [saveSettings]
  )

  const fetchDemoUserToken = useCallback(async () => {
    const userDetails = getUserDetails()

    if (userDetails) {
      if (
        userDetails?.roles?.toLowerCase() !== ROLES.DEMO &&
        userDetails?.roles?.toLowerCase() !== ROLES.ADMIN
      ) {
        clearLocalAndSessionStorage()
        navigateToHome()
        setLoading((prev) => ({ ...prev, demoUserToken: false }))
        return
      } else {
        fetchUserSubscription()
        setLoading((prev) => ({ ...prev, demoUserToken: false }))
        return
      }
    }

    const response = await getDemoUserToken()

    if (response?.isError) {
      showErrorToast(response)
      setLoading((prev) => ({ ...prev, demoUserToken: false }))
      return
    }

    dispatch(setSelectedWorkspace(response?.workspaceId))
    dispatch(setUserInformation({ jwt: response?.token }))

    setLoading((prev) => ({ ...prev, demoUserToken: false }))

    fetchUserSubscription()
  }, [navigateToHome, dispatch, fetchUserSubscription])

  // Effect to set the cookie on tenant change
  useEffect(() => {
    if (isPublicRoute) {
      return
    }

    const workspaceId = getCookie('workspaceId')
    const token = getCookie('user-token')
    const rememberMeFlag = getCookie('remember-me-flag') || false
    const candidateAppMode = getCookie(CANDIDATE_APP_MODE_COOKIE_NAME) || null
    const appMode = getCookie('app-mode')

    if (token) {
      // setting to local storage
      setRememberMeFlag(JSON.parse(rememberMeFlag))
      dispatch(setSelectedWorkspace(workspaceId))
      dispatch(setUserInformation({ jwt: token }))
      if (appMode) {
        dispatch(setAppMode(appMode))
      }

      if (candidateAppMode) {
        dispatch(setCandidateAppMode(candidateAppMode))
      }

      // clearing cross-subdomain cookies
      deleteCookie('workspaceId', `.${DOMAIN}`)
      deleteCookie('user-token', `.${DOMAIN}`)
      deleteCookie('remember-me-flag', `.${DOMAIN}`)
      deleteCookie(CANDIDATE_APP_MODE_COOKIE_NAME, `.${DOMAIN}`)
      deleteCookie('app-mode', `${DOMAIN}`)
    }
  }, [dispatch, isPublicRoute])

  // effect to fetch subscriptions
  useEffect(() => {
    if (isDemoTenant) {
      return
    }

    if (!checkCandidateAccess() && !checkDeveloperAccess() && !isPublicRoute) {
      fetchUserSubscription()
    } else {
      setLoading((prev) => ({ ...prev, subscription: false }))
    }
  }, [fetchUserSubscription, isPublicRoute, isDemoTenant])

  useEffect(() => {
    if (isPublicRoute) {
      setLoading((prev) => ({ ...prev, config: false }))
      return
    }

    if (!checkDeveloperAccess()) {
      fetchTenantConfig(contestUri)
    } else {
      setLoading((prev) => ({ ...prev, config: false }))
    }
  }, [contestUri, fetchTenantConfig, isPublicRoute])

  useEffect(() => {
    const favicon = document.getElementById('favicon')
    if (favicon) {
      const newFavicon = settings?.tenantConfig?.favicon || DefaultFavicon
      favicon.href = newFavicon
    }
  }, [settings?.tenantConfig?.favicon])

  useEffect(() => {
    if (isPublicRoute || !isDemoTenant) {
      setLoading((prev) => ({ ...prev, demoUserToken: false }))
      return
    }

    fetchDemoUserToken()
  }, [isPublicRoute, fetchDemoUserToken, location.pathname, isDemoTenant])

  return (
    <Fragment>
      <ErrorBoundary>
        {!loading.config && !loading.subscription && !loading.demoUserToken && (
          <Suspense fallback={<OverlayLoading />}>
            <Outlet />
          </Suspense>
        )}
      </ErrorBoundary>
      <ToastContainer
        closeOnClick
        autoClose={3000}
        position="top-right"
        hideProgressBar={true}
      />

      {!isPublicRoute && <TenantDetailsModal />}
    </Fragment>
  )
}

export default App
